import {
  DEFAULT_LOGO_SORT_KEY,
  DEFAULT_LOGO_SORT_ORDER,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/constants';
import { GroupedPresencesType } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/hooks/useBatchSelection';
import { TSegmentLogo } from '@/features/segments/types';
import { SortOrder } from '@/types';

type GroupedItem<T> = { id: any; children: T[] };
type MixedItem<T> = T | GroupedItem<T>;

const compareValues = (a: any, b: any, direction: SortOrder): number => {
  if (a === b) return 0;

  // Handle null/undefined for safety (send them to the end)
  if (a === undefined || a === null) return 1;
  if (b === undefined || b === null) return -1;

  // Standard comparison (works for ISO dates, numbers, and strings)
  if (direction === 'asc') {
    return a < b ? -1 : 1;
  }

  return a > b ? -1 : 1;
};

/**
 * Type Guard: Determines if an item is a Group { id, children }
 * Checks if the 'children' property exists and is an array.
 */
const isGroup = <T>(item: MixedItem<T>): item is GroupedItem<T> => {
  return (
    typeof item === 'object' &&
    item !== null &&
    'children' in item &&
    Array.isArray((item as any).children)
  );
};

/**
 * Generic sorting method
 */
export const sortMixedCollection = <T>(
  items: MixedItem<T>[],
  key: keyof T,
  direction: SortOrder = 'asc'
): MixedItem<T>[] => {
  // 1. Sort children within each group (clone to avoid mutating references)
  const prepared = items.map(item => {
    if (isGroup(item)) {
      const sortedChildren = [...item.children].sort((a, b) =>
        compareValues(a[key], b[key], direction)
      );
      return { ...item, children: sortedChildren };
    }
    return item;
  });

  // 2. Sort all items together using each item's representative value.
  //    For groups, use the first child's value (all children share the same entity).
  //    For singles, use their own value directly.
  //    This keeps the order stable regardless of whether an item is a group or a single,
  //    so transitions between the two states (e.g. after delete/move) don't cause jumps.
  return [...prepared].sort((a, b) => {
    const va = isGroup(a) ? a.children[0]?.[key] : (a as T)[key];
    const vb = isGroup(b) ? b.children[0]?.[key] : (b as T)[key];
    return compareValues(va, vb, direction);
  });
};

export const isSegmentLogo = (item: MixedItem<TSegmentLogo>): item is TSegmentLogo =>
  'logo_id' in item;

/**
 * Sort grouped presences (logos or people)
 */
export const sortGroupPresences = (
  items: GroupedPresencesType,
  key: string = DEFAULT_LOGO_SORT_KEY,
  direction: SortOrder = DEFAULT_LOGO_SORT_ORDER
) => {
  return sortMixedCollection(items as any[], key as any, direction);
};

/** @deprecated use sortGroupPresences */
export const sortGroupLogos = sortGroupPresences;
